import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQuery } from './createBaseQuery';

const GENERATE_SCENARIO_ENDPOINT = '/tools/llm-wizard/generateScenario';

export interface IGenerateScenarioRequest {
  scenarioIdea?: string;
  playerName?: string;
  aiNames?: string[];
  setup?: string;
  characters?: Record<string, unknown>;
  location?: string;
  initialMessages?: Record<string, unknown>[];
  avatars?: Record<string, unknown>;
  appearance?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export interface IGenerateScenarioResponse {
  scenarioIdea?: string;
  title?: string;
  aiNames?: { 'ai-names': string[]; 'player-name': string };
  characters?: Record<string, unknown>;
  setup?: string;
  location?: string;
  initialMessages?: Record<string, unknown>[];
  scenario_config?: Record<string, unknown>;
}

export const generateScenarioApi = createApi({
  reducerPath: 'generateScenarioApi',
  baseQuery: createBaseQuery(),
  endpoints: (builder) => ({
    generateIdea: builder.mutation<
      string,
      Pick<IGenerateScenarioRequest, 'playerName' | 'config'> & { keywords?: string[] }
    >({
      query: ({ keywords = [], playerName, config = {} }) => ({
        url: `${GENERATE_SCENARIO_ENDPOINT}/generateIdea`,
        method: 'POST',
        body: { data: { keywords, 'player-name': playerName, config } },
      }),
      transformResponse: (response: { result: { scenarioIdea: string } }) => response.result.scenarioIdea,
    }),

    generateTitle: builder.mutation<string, Pick<IGenerateScenarioRequest, 'scenarioIdea' | 'playerName' | 'aiNames' | 'setup' | 'config'>>({
      query: ({ scenarioIdea, playerName, aiNames = [], setup, config = {} }) => ({
        url: `${GENERATE_SCENARIO_ENDPOINT}/generateTitle`,
        method: 'POST',
        body: { data: { scenarioIdea, 'player-name': playerName, 'ai-names': aiNames, setup, config } },
      }),
      transformResponse: (response: { result: { title: string } }) => response.result.title,
    }),

    requestAiNames: builder.mutation<IGenerateScenarioResponse['aiNames'], Pick<IGenerateScenarioRequest, 'scenarioIdea' | 'playerName' | 'config'>>({
      query: ({ scenarioIdea, playerName, config = {} }) => ({
        url: `${GENERATE_SCENARIO_ENDPOINT}/requestAiNames`,
        method: 'POST',
        body: { data: { scenarioIdea, 'player-name': playerName, config } },
      }),
      transformResponse: (response: { result: IGenerateScenarioResponse['aiNames'] }) => response.result,
    }),

    requestCharacters: builder.mutation<IGenerateScenarioResponse['characters'], Pick<IGenerateScenarioRequest, 'scenarioIdea' | 'playerName' | 'aiNames' | 'config'>>({
      query: ({ scenarioIdea, playerName, aiNames = [], config = {} }) => ({
        url: `${GENERATE_SCENARIO_ENDPOINT}/requestCharacters`,
        method: 'POST',
        body: { data: { scenarioIdea, 'player-name': playerName, 'ai-names': aiNames, config } },
      }),
      transformResponse: (response: { result: IGenerateScenarioResponse['characters'] }) => response.result,
    }),

    generateAvatar: builder.mutation<{ url: string; appearance: string }, { characterDescription: string }>({
      query: ({ characterDescription }) => ({
        url: '/tools/generate-character-image',
        method: 'POST',
        body: { text: characterDescription },
      }),
      transformResponse: (response: { url: string, appearance: string }) => ({
        url: response.url,
        appearance: response.appearance,
      }),
    }),
    generateAvatarWithName: builder.mutation<{ url: string; name: string; appearance: string }, { characterDescription: string; name: string }>({
      query: ({ characterDescription }) => ({
        url: '/tools/generate-character-image',
        method: 'POST',
        body: { text: characterDescription },
      }),
      transformResponse: (response: { url: string, appearance: string }, meta, arg) => ({
        url: response.url,
        appearance: response.appearance,
        name: arg.name,
      }),
    }),

    requestSetup: builder.mutation<string, Pick<IGenerateScenarioRequest, 'scenarioIdea' | 'playerName' | 'aiNames' | 'characters' | 'config'>>({
      query: ({ scenarioIdea, playerName, aiNames = [], characters = {}, config = {} }) => ({
        url: `${GENERATE_SCENARIO_ENDPOINT}/requestSetup`,
        method: 'POST',
        body: { data: { scenarioIdea, 'player-name': playerName, 'ai-names': aiNames, characters, config } },
      }),
      transformResponse: (response: { result: { setup: string } }) => response.result.setup,
    }),

    requestLocation: builder.mutation<string, Pick<IGenerateScenarioRequest, 'scenarioIdea' | 'playerName' | 'setup' | 'characters' | 'config'>>({
      query: ({ scenarioIdea, playerName, setup, characters = {}, config = {} }) => ({
        url: `${GENERATE_SCENARIO_ENDPOINT}/requestLocation`,
        method: 'POST',
        body: { data: { scenarioIdea, 'player-name': playerName, setup, characters, config } },
      }),
      transformResponse: (response: { result: { location: string } }) => response.result.location,
    }),

    requestInitialMessages: builder.mutation<IGenerateScenarioResponse['initialMessages'], Omit<IGenerateScenarioRequest, 'avatars'>>({
      query: ({ scenarioIdea, playerName, aiNames = [], setup, characters = {}, location = '', config = {} }) => ({
        url: `${GENERATE_SCENARIO_ENDPOINT}/requestInitialMessages`,
        method: 'POST',
        body: { data: { scenarioIdea, 'player-name': playerName, 'ai-names': aiNames, setup, characters, location, config } },
      }),
      transformResponse: (response: { result: { initialMessages: IGenerateScenarioResponse['initialMessages'] } }) => response.result.initialMessages,
    }),

    requestScenario: builder.mutation<IGenerateScenarioResponse['scenario_config'], IGenerateScenarioRequest>({
      query: ({ scenarioIdea, playerName, aiNames = [], setup, characters = {}, location = '', initialMessages = [], avatars = {}, appearance = {}, config = {} }) => ({
        url: `${GENERATE_SCENARIO_ENDPOINT}/requestScenario`,
        method: 'POST',
        body: { data: { scenarioIdea, 'player-name': playerName, 'ai-names': aiNames, setup, characters, location, initialMessages, avatars, appearance, config } },
      }),
      transformResponse: (response: { result: { scenario_config: IGenerateScenarioResponse['scenario_config'] } }) => response.result.scenario_config,
    }),

    isLegal: builder.mutation<boolean, Record<string, unknown>>({
      query: (data) => ({
        url: '/check-if-legal',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: { result: boolean }) => response.result,
    }),

    generateAppearance: builder.mutation<string, { text: string, name: string }>({
      query: ({ text, name }) => ({
        url: '/tools/generate-appearance',
        method: 'POST',
        body: { text, name },
      }),
    }),

    generateAvatarQueued: builder.mutation<{ url: string }, { appearance: string }>({
      query: ({ appearance }) => ({
        url: '/tools/generate-avatar',
        method: 'POST',
        body: { appearance },
      }),
      transformResponse: (response: { url: string }) => ({
        url: response.url,
      }),
    }),
  }),
});

export const {
  useGenerateIdeaMutation,
  useGenerateTitleMutation,
  useRequestAiNamesMutation,
  useRequestCharactersMutation,
  useGenerateAvatarMutation,
  useGenerateAvatarWithNameMutation,
  useRequestSetupMutation,
  useRequestLocationMutation,
  useRequestInitialMessagesMutation,
  useRequestScenarioMutation,
  useIsLegalMutation,
  useGenerateAppearanceMutation,
  useGenerateAvatarQueuedMutation,
} = generateScenarioApi;
